import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity, BuildingAnalyticEntityStyle } from "building-types/buildingAnalyticEntityTypes";
import type { AnalyticMaterial } from "building-types/buildingAnalyticEntityTypes";
import {
  buildingGeomAttributeTagNew,
  TAG_CONDITION_FORCE_DOUBLE_SIDED,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingEntityHandleBase } from "building-types/entities/buildingEntityHandle";
import type { CeilingHandle, DocEntityHandle, LevelHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomCondition } from "building-types/rooms/roomTypesEnum";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import type { RWTable, Table } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { faceCopy, faceMove, faceNew, faceRepairLoopDirections, type Face } from "geom/brep";
import { classifyContainmentVec2PolyLine2MultiPolygonFast, getPointInPolygon } from "geom/containment";
import { extrudeFace, extrusionToBRep } from "geom/extrude";
import { planeNewXY, planeReProjectPolyLine2 } from "geom/plane";
import { splitFaceWithFaces } from "geom/polyclip/trimFaceWithFaces";
import type { PolyLine2MultiPolygon } from "geom/polyLine2Types";
import { sortedLevels } from "../elements/level/levelTools";
import { addAnalyticFace } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";
import { getColorForRoomConditionAdjacency } from "./floorConditionAdjacencyDebugColor";

const TRIM_FACE_HEIGHT = 1000;
const TRIM_FACE_OFFSET = TRIM_FACE_HEIGHT / 2;

const CEILING_STYLE: BuildingAnalyticEntityStyle = {
  opacity: percentNew(0.5),
  lineWidth3d: worldInchesNew(1),
};

interface GenericCeilingFace {
  readonly face: Face;
  readonly material: AnalyticMaterial;
  readonly levelHandle: LevelHandle;
  readonly generatedCeilingHandle: BuildingEntityHandleBase<"CEILING">;
  readonly subCeilingHandle?: CeilingHandle;
}

export function addCeilingAnalyticEntities(building: ReturnType<typeof select>): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};

  const ceilingFacesByLevelId: Record<string, GenericCeilingFace[]> = collectGenericCeilingFacesByLevel(building);

  const { levels, floorAreaTakeoffs } = building;

  const sortedLevelsArray = sortedLevels(levels);

  const conditionedAreas = floorAreaTakeoffs[FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA];

  for (let i = 0; i < sortedLevelsArray.length; i++) {
    const currLevel = sortedLevelsArray[i];
    const currLevelId = currLevel.id;
    const ceilingFaces = ceilingFacesByLevelId[currLevelId];

    if (!ceilingFaces) {
      continue;
    }

    const nextLevelId: string | undefined = sortedLevelsArray[i + 1]?.id;

    const belowCondAreas: PolyLine2MultiPolygon =
      conditionedAreas?.levelFloorAreas[currLevelId]?.floorAreaPolygons ?? [];
    const aboveCondAreas: PolyLine2MultiPolygon = nextLevelId
      ? conditionedAreas?.levelFloorAreas[nextLevelId]?.floorAreaPolygons ?? []
      : [];

    if (belowCondAreas.length === 0 && aboveCondAreas.length === 0) {
      for (const ceiling of ceilingFaces) {
        addAnalyticCeiling(entities, ceiling, RoomCondition.UNCONDITIONED, RoomCondition.UNCONDITIONED);
      }
      continue;
    }

    const aboveTrimFaces = extrudePolygonsToTrimFaces(aboveCondAreas);
    const belowTrimFaces = extrudePolygonsToTrimFaces(belowCondAreas);

    for (const ceiling of ceilingFaces) {
      const splitCeilingFaces = splitFaceWithFaces(ceiling.face, aboveTrimFaces)
        .faces.map((f) => splitFaceWithFaces(f, belowTrimFaces).faces)
        .flat();

      for (const face of splitCeilingFaces) {
        const outline = planeReProjectPolyLine2(face.loops[0], ceiling.face.plane, planeNewXY());
        const testPt = getPointInPolygon(outline);
        if (!testPt) {
          continue;
        }

        const [x, y] = testPt;

        const conditionAbove = classifyContainmentVec2PolyLine2MultiPolygonFast(x, y, aboveCondAreas);
        const conditionBelow = classifyContainmentVec2PolyLine2MultiPolygonFast(x, y, belowCondAreas);
        addAnalyticCeiling(
          entities,
          { ...ceiling, face },
          conditionAbove ? RoomCondition.CONDITIONED : RoomCondition.UNCONDITIONED,
          conditionBelow ? RoomCondition.CONDITIONED : RoomCondition.UNCONDITIONED,
        );
      }
    }
  }

  return entities;
}

function extrudePolygonsToTrimFaces(areas: PolyLine2MultiPolygon): Face[] {
  const trimFaces: Face[] = [];
  for (const loop of areas) {
    const face = faceRepairLoopDirections(faceNew(loop, planeNewXY()));
    const brep = extrusionToBRep(
      extrudeFace(faceMove(faceCopy(face), 0, 0, TRIM_FACE_OFFSET), 0, 0, -TRIM_FACE_HEIGHT),
    );
    const [, , ...brepTrimFaces] = brep.faces;
    trimFaces.push(...brepTrimFaces);
  }
  return trimFaces;
}

function addAnalyticCeiling(
  entities: RWTable<BuildingAnalyticEntity>,
  ceiling: GenericCeilingFace,
  conditionAbove: RoomCondition,
  conditionBelow: RoomCondition,
) {
  const { face, material, levelHandle, generatedCeilingHandle, subCeilingHandle } = ceiling;

  const parentHandles: DocEntityHandle[] = [levelHandle, { type: "GENERATED_ENTITY", entity: generatedCeilingHandle }];

  if (subCeilingHandle) {
    parentHandles.push(subCeilingHandle);
  }

  addAnalyticFace(
    "ANALYTIC_CEILING",
    entities,
    "CEILING",
    parentHandles,
    face,
    material,
    [
      buildingGeomAttributeTagNew("conditionAbove", conditionAbove, "STRING"),
      buildingGeomAttributeTagNew("conditionBelow", conditionBelow, "STRING"),
    ],
    { ...CEILING_STYLE, color: getColorForRoomConditionAdjacency(conditionAbove, conditionBelow) },
  );
}

function collectGenericCeilingFacesByLevel(building: ReturnType<typeof select>): Record<string, GenericCeilingFace[]> {
  const ceilingFacesByLevelId: Record<string, GenericCeilingFace[]> = {};

  const { rooms, ceilings } = building;

  for (const roomId in ceilings) {
    const ceiling = ceilings[roomId];

    // Get the room to find the level ID (ceiling id is the room id)
    const room = rooms[roomId];
    if (!room) {
      continue;
    }

    const levelHandle: LevelHandle = { type: "LEVEL", id: room.level.id };

    if (!ceilingFacesByLevelId[levelHandle.id]) {
      ceilingFacesByLevelId[levelHandle.id] = [];
    }

    const generatedCeilingHandle: BuildingEntityHandleBase<"CEILING"> = { id: roomId, entityType: "CEILING" };

    const material: AnalyticMaterial = {
      materialSrc: ceiling.section.designMaterialId,
    };
    const filteredFaces = ceiling.section.ceilingFaces.filter(
      (face) => !face.tags?.includes(TAG_CONDITION_FORCE_DOUBLE_SIDED),
    );
    for (const face of filteredFaces) {
      ceilingFacesByLevelId[levelHandle.id].push({ face, material, levelHandle, generatedCeilingHandle });
    }

    for (const subCeiling of ceiling.subCeilings) {
      for (const section of subCeiling.sections) {
        const material: AnalyticMaterial = {
          materialSrc: section.designMaterialId,
        };
        const filteredFaces = section.ceilingFaces.filter(
          (face) => !face.tags?.includes(TAG_CONDITION_FORCE_DOUBLE_SIDED),
        );
        for (const face of filteredFaces) {
          ceilingFacesByLevelId[levelHandle.id].push({
            face,
            material,
            levelHandle,
            generatedCeilingHandle,
            subCeilingHandle: subCeiling.handle,
          });
        }
      }
    }
  }

  return ceilingFacesByLevelId;
}

const select = makeBuildingSelector(["ceilings", "rooms", "levels", "floorAreaTakeoffs"]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticCeilingEntities"> {
  return { analyticCeilingEntities: addCeilingAnalyticEntities(building) };
}

export default memo(select, generate);
